import React from 'react';
import {
    alpha,
    Box,
    Chip,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    AccountTree,
    Api,
    ArrowForward,
    Business,
    CheckCircle,
    Hub,
    Inventory2,
    Restaurant,
    Science,
    SettingsEthernet,
    Storage,
    SyncAlt,
} from '@mui/icons-material';

const sourceSystems = [
    {
        label: 'SAP / ERP',
        detail: 'Product and production data',
        icon: <Business sx={{ fontSize: 15 }} />,
    },
    {
        label: 'Legacy databases',
        detail: 'Historical product records',
        icon: <Storage sx={{ fontSize: 15 }} />,
    },
    {
        label: 'Internal files',
        detail: 'Structured exports and datasets',
        icon: <AccountTree sx={{ fontSize: 15 }} />,
    },
];

const integrationSteps = [
    {
        label: 'Custom connector',
        detail: 'API or file-based integration',
        icon: <Api sx={{ fontSize: 15 }} />,
    },
    {
        label: 'Mapping rules',
        detail: 'Fields aligned with CibusAI data models',
        icon: <SettingsEthernet sx={{ fontSize: 15 }} />,
    },
    {
        label: 'Data migration',
        detail: 'Legacy records prepared for review',
        icon: <SyncAlt sx={{ fontSize: 15 }} />,
    },
];

const cibusOutputs = [
    {
        label: 'Product library',
        value: 'Products',
        icon: <Restaurant sx={{ fontSize: 15 }} />,
    },
    {
        label: 'Formulation records',
        value: 'Formulations',
        icon: <Science sx={{ fontSize: 15 }} />,
    },
    {
        label: 'Ingredient library',
        value: 'Ingredients',
        icon: <Inventory2 sx={{ fontSize: 15 }} />,
    },
];

function StatusPill({ children }) {
    return (
        <Box
            sx={{
                px: 0.7,
                py: 0.3,
                borderRadius: 999,
                fontSize: 9.5,
                fontWeight: 900,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                color: 'success.dark',
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.success.main, 0.28),
            }}
        >
            {children}
        </Box>
    );
}

function SourceCard({ item }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0.95,
                borderRadius: 2,
                bgcolor: 'background.paper',
                minWidth: 0,
            }}
        >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                    sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {item.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            fontWeight: 900,
                            fontSize: 10.5,
                            lineHeight: 1.15,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.label}
                    </Typography>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            fontWeight: 700,
                            fontSize: 9.5,
                            lineHeight: 1.2,
                            mt: 0.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.detail}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

function IntegrationStep({ item }) {
    return (
        <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
                px: 0.9,
                py: 0.75,
                borderRadius: 1.6,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.055),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: 25,
                    height: 25,
                    borderRadius: '50%',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {item.icon}
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        fontWeight: 900,
                        fontSize: 10.3,
                        lineHeight: 1.15,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {item.label}
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: 'block',
                        fontWeight: 700,
                        fontSize: 9.4,
                        lineHeight: 1.2,
                        mt: 0.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {item.detail}
                </Typography>
            </Box>
        </Stack>
    );
}

function OutputCard({ item }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0.9,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                minWidth: 0,
            }}
        >
            <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                    sx={{
                        width: 25,
                        height: 25,
                        borderRadius: '50%',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {item.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            fontSize: 9.4,
                            fontWeight: 800,
                            lineHeight: 1.1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.label}
                    </Typography>

                    <Typography
                        sx={{
                            mt: 0.25,
                            fontSize: 13.5,
                            lineHeight: 1.1,
                            fontWeight: 900,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.value}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

export default function EnterpriseErpIntegrationVisual() {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 300,
                p: 1.6,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 1.7,
                        py: 1.2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        minWidth: 0,
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Hub sx={{ fontSize: 16 }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Enterprise and ERP integration
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    mt: 0.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Connect SAP, ERP and legacy data sources to CibusAI
                            </Typography>
                        </Box>
                    </Stack>

                    <Chip
                        label="Custom integration"
                        size="small"
                        sx={{
                            height: 23,
                            fontSize: 10.5,
                            fontWeight: 800,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            flexShrink: 0,
                            ml: 1,
                        }}
                    />
                </Stack>

                {/* Body */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'minmax(0, 0.95fr) auto minmax(0, 1.05fr) auto minmax(0, 0.95fr)',
                        },
                        gap: 1,
                        p: 1.6,
                        minHeight: 245,
                        alignItems: 'stretch',
                        overflow: 'hidden',
                    }}
                >
                    {/* Source systems */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.15,
                            borderRadius: 2,
                            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mb: 1,
                                fontWeight: 900,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.35,
                            }}
                        >
                            Source systems
                        </Typography>

                        <Stack spacing={0.75}>
                            {sourceSystems.map((item) => (
                                <SourceCard key={item.label} item={item} />
                            ))}
                        </Stack>
                    </Paper>

                    <Box
                        sx={{
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ArrowForward sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>

                    {/* Integration layer */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.15,
                            borderRadius: 2,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                            minWidth: 0,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 900,
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.35,
                                }}
                            >
                                Integration layer
                            </Typography>

                            <StatusPill>Configured</StatusPill>
                        </Stack>

                        <Stack spacing={0.75}>
                            {integrationSteps.map((item) => (
                                <IntegrationStep key={item.label} item={item} />
                            ))}
                        </Stack>
                    </Paper>

                    <Box
                        sx={{
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ArrowForward sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>

                    {/* CibusAI */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.15,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mb: 1,
                                fontWeight: 900,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.35,
                            }}
                        >
                            CibusAI records
                        </Typography>

                        <Stack spacing={0.75}>
                            {cibusOutputs.map((item) => (
                                <OutputCard key={item.label} item={item} />
                            ))}
                        </Stack>
                    </Paper>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        px: 1.7,
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.018),
                    }}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <CheckCircle sx={{ fontSize: 15, color: 'success.main', flexShrink: 0 }} />

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontSize: 10.4,
                                fontWeight: 700,
                                lineHeight: 1.3,
                            }}
                        >
                            Enterprise integrations are configured around each client&apos;s existing systems, data structure and migration needs.
                        </Typography>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}